import React from 'react';
import { motion } from 'framer-motion';
import SourceDisplay from './SourceDisplay';
import { DocumentSource } from '../../services/streamingService';

export type Role = 'user' | 'assistant' | 'system';

export interface Message {
  id: string;
  role: Role;
  content: string;
  timestamp: number;
  sources?: DocumentSource[];
  confidence?: number;
  processingTime?: number;
  tokenCount?: number;
  isStreaming?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  onCitationClick?: (documentId: string, documentName: string, chunks: DocumentSource[]) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onCitationClick }) => {
  const isUser = message.role === 'user';
  const bubbleClasses = isUser
    ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm'
    : 'bg-white text-gray-900 border border-gray-200 rounded-2xl rounded-tl-sm';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full min-w-0`}
    >
      <div className={`max-w-[85%] md:max-w-[70%] min-w-0 message-bubble-container`}>
        <div className={`px-4 py-3 shadow-sm min-w-0 ${bubbleClasses}`}>
          <div className="message-content force-text-wrap leading-relaxed break-words">
            {message.content}
            {message.isStreaming && (
              <span className="inline-block w-2 h-5 bg-current animate-pulse ml-1" />
            )}
          </div>
          <div className={`mt-2 text-xs ${isUser ? 'text-blue-100' : 'text-gray-400'}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        
        {/* Show sources for assistant messages */}
        {!isUser && message.sources && message.sources.length > 0 && (
          <SourceDisplay
            sources={message.sources}
            confidence={message.confidence}
            processingTime={message.processingTime}
            tokenCount={message.tokenCount}
            onCitationClick={onCitationClick}
            className="mt-2"
          />
        )}
      </div>
    </motion.div>
  );
};

export default MessageBubble;
